import { LINK_TYPES, type Issue, type LinkType } from "@gjwtrack/domain";
import { fieldKey } from "@gjwtrack/query-language";
import { X } from "lucide-react";
import { useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { client, useClient } from "../lib/client.ts";
import { BUNDLE_KINDS, can, issueKey, Permission } from "../lib/format.ts";
import { Avatar, ValueBadge } from "./Badges.tsx";
import { FieldRow, PanelValue } from "./FieldPanel.tsx";
import { MarkdownEditor } from "./MarkdownEditor.tsx";
import { Button, Dialog } from "../ui/ring.ts";
import { ValueSelect } from "../ui/ValueSelect.tsx";
import { uuid } from "@gjwtrack/client";

type Values = Record<string, ReadonlyArray<number | string>>;

/** "New issue ▾" on a detail page: the created issue is linked to `issueId`; `outward` = the label names the new issue's side. */
export interface LinkTo {
  issueId: number;
  type: LinkType;
  outward: boolean;
}

/** New issue, laid out like YouTrack's: near full-screen, big summary, Markdown editor, the field panel on the right. */
export function NewIssueDialog({ onClose, onCreated, draftId: initialDraftId = null, linkTo }: { onClose: () => void; onCreated: (issue: Issue) => void; draftId?: number | null; linkTo?: LinkTo }) {
  const schema = useClient((s) => s.schema);
  const linkTarget = useClient((s) => (linkTo ? s.display[linkTo.issueId] : undefined));
  const me = useClient((s) => s.me);
  const drafts = useClient((s) => s.drafts);
  const [draftId, setDraftId] = useState<number | null>(initialDraftId);
  const [restored, setRestored] = useState(initialDraftId === null);
  // The upload resolver runs outside render, and needs the id the last save produced.
  const draftIdRef = useRef<number | null>(initialDraftId);
  const projects = useMemo(() => (schema?.data.projects ?? []).filter((p) => can(me, p.id, Permission.CREATE_ISSUE)), [schema, me]);
  const [projectId, setProjectId] = useState<number | null>(projects[0]?.id ?? null);
  const [summary, setSummary] = useState("");
  const [description, setDescription] = useState("");
  const [fields, setFields] = useState<Values>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const projectFields = useMemo(() => (projectId === null ? [] : (schema?.projectFieldsByProject.get(projectId) ?? [])), [schema, projectId]);
  useEffect(() => {
    if (!restored) return;
    const defaults: Values = {};
    for (const pf of projectFields) if (pf.defaultValue) defaults[fieldKey(pf.fieldId)] = pf.defaultValue;
    setFields((cur) => (Object.keys(cur).length ? cur : defaults));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectFields, restored]);

  // Opening a draft: restore its contents once the list is known.
  useEffect(() => {
    if (restored || initialDraftId === null) return;
    const d = drafts.find((x) => x.id === initialDraftId);
    if (!d) return;
    setProjectId(d.projectId ?? projects[0]?.id ?? null);
    setSummary(d.summary);
    setDescription(d.description ?? "");
    setFields(d.fields as Values);
    setRestored(true);
  }, [drafts, initialDraftId, restored, projects]);

  // Autosave as a draft (server-side, like YouTrack) a second after typing stops.
  useEffect(() => {
    if (!restored || busy) return;
    if (!summary.trim() && !description.trim()) return;
    const timer = setTimeout(() => {
      client.http
        .command<{ id: number }>("saveDraft", { draftId: draftIdRef.current, projectId, summary, description: description || null, fields }, uuid())
        .then((d) => {
          draftIdRef.current = d.id;
          if (d.id !== draftId) setDraftId(d.id);
          void client.loadDrafts();
        })
        .catch(() => {});
    }, 1000);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [summary, description, fields, projectId]);

  /**
   * Files dropped into the description go on the draft, so there has to be one:
   * save it now if the autosave has not run yet. `createIssue` moves them over.
   */
  const attachTarget = async (): Promise<{ draftId: number } | null> => {
    if (draftIdRef.current !== null) return { draftId: draftIdRef.current };
    const d = await client.http.command<{ id: number }>("saveDraft", { draftId: null, projectId, summary, description: description || null, fields }, uuid());
    draftIdRef.current = d.id;
    setDraftId(d.id);
    void client.loadDrafts();
    return { draftId: d.id };
  };

  const submit = async (e?: FormEvent): Promise<void> => {
    e?.preventDefault();
    if (busy || projectId === null || !summary.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const issue = await client.createIssue({ projectId, summary: summary.trim(), description: description.trim() || null, fields, visibility: null, draftId: draftIdRef.current });
      if (linkTo) {
        // The label named the new issue's side of the link: "subtask of" → new → current; "parent for" → current → new.
        const [from, to] = linkTo.outward ? [issue.id, linkTo.issueId] : [linkTo.issueId, issue.id];
        await client.linkIssues(from, to, linkTo.type).catch(() => {});
      }
      draftIdRef.current = null;
      void client.loadDrafts();
      onCreated(issue);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  if (!schema) return null;
  const project = projectId === null ? undefined : schema.projectsById.get(projectId);
  const canCreate = !busy && projectId !== null && summary.trim().length > 0;
  return (
    // Ring Dialog: overlay, Esc (popups first — Ring stacks shortcut scopes), focus trap, portal. Sized to YouTrack's proportions.
    <Dialog show label="New issue" onCloseAttempt={onClose} showCloseButton={false} trapFocus contentClassName="!w-[75rem] !max-w-[calc(100vw-2rem)] !p-0 !overflow-hidden">
      <form onSubmit={(e) => void submit(e)} className="flex h-[min(82vh,58rem)] w-full flex-col overflow-hidden" aria-label="New issue">
        {projects.length === 0 ? (
          <p className="p-4 text-muted">You cannot create issues in any project.</p>
        ) : (
          <div className="flex min-h-0 flex-1">
            <div className="flex min-w-0 flex-1 flex-col overflow-y-auto px-6 pt-5">
              <div className="flex items-start gap-3">
                <input
                  className="summary-input min-w-0 flex-1"
                  placeholder="Summary"
                  autoFocus
                  value={summary}
                  onChange={(e) => setSummary(e.target.value)}
                />
                <Button icon={X} title="Close" onClick={onClose} />
              </div>
              {linkTo && (
                <div className="mt-2 text-[13px] text-muted">
                  Will be linked to <span className="text-value">{linkTarget ? issueKey(schema, linkTarget) : `#${linkTo.issueId}`}</span> as{" "}
                  <span className="text-ink">{linkTo.outward ? LINK_TYPES[linkTo.type].outward : LINK_TYPES[linkTo.type].inward}</span>
                </div>
              )}
              <div className="mt-4 flex min-h-0 flex-1 flex-col">
                <MarkdownEditor key={String(restored)} initial={description} placeholder="Description (optional)" hideFooter fill target={attachTarget} onChange={setDescription} onSave={() => void submit()} onCancel={onClose} />
              </div>
              {error && <div className="mt-3 rounded bg-danger-soft px-2 py-1 text-danger">{error}</div>}
              <div className="flex shrink-0 items-center gap-2 py-4">
                <Button primary type="submit" disabled={!canCreate}>
                  Create
                </Button>
                <Button onClick={onClose}>Cancel</Button>
                <span className="ml-2 text-xs text-faint">⌘/Ctrl+Enter to create</span>
              </div>
            </div>
            <aside className="w-70 shrink-0 overflow-y-auto border-l border-line bg-ground px-5 py-3">
              <FieldRow label="Project" badge={<span className="badge chip-1">{(project?.key ?? "?")[0]}</span>}>
                <ValueSelect
                  inline
                  options={projects.map((p) => ({ id: p.id, label: `${p.key} · ${p.name}` }))}
                  selected={new Set(projectId === null ? [] : [projectId])}
                  multi={false}
                  emptyLabel="Choose a project"
                  onChange={(ids) => ids[0] !== undefined && setProjectId(ids[0])}
                />
              </FieldRow>
              {projectFields.map((pf) => {
                const def = schema.fieldsById.get(pf.fieldId);
                if (!def) return null;
                const key = fieldKey(def.id);
                const values = fields[key] ?? [];
                const first = values[0];
                const badge =
                  first === undefined ? null : BUNDLE_KINDS.has(def.kind) ? <ValueBadge schema={schema} def={def} value={first} /> : def.kind === "user" && typeof first === "number" ? <Avatar schema={schema} userId={first} /> : null;
                return (
                  <FieldRow key={key} label={pf.alias ?? def.name} required={pf.required} badge={badge}>
                    <PanelValue schema={schema} def={def} pf={pf} values={values} editable onChange={(v) => setFields((f) => ({ ...f, [key]: v }))} />
                  </FieldRow>
                );
              })}
            </aside>
          </div>
        )}
      </form>
    </Dialog>
  );
}
